import { FunctionComponent, useEffect } from 'react';
import { useSelector, useStore } from 'react-redux';
import CSSTransition from 'react-transition-group/CSSTransition';
import { Draft, SharingDraft } from '@/types';
import { TrackingFeatureTypes, trackFeature } from '@/utils/adobeLaunch';
import { t } from '@/utils/i18n';
import { isCYOMode } from '@/utils/params';
import { addProductToHistory } from '@/utils/productHistory';
import { getConfigurations } from '@/model/draft/getConfigurations';
import { getConfigurationsOnView } from '@/model/draft/getConfigurationsOnView';
import { getProductType } from '@/model/draft/getProductType';
import { getPrintAreaForView } from '@/model/productType/getPrintAreaForView';
import { getIsLoggedIn } from '@/redux/core/getters/getIsLoggedIn';
import { setDraftAction } from '@/redux/draft/actions/setDraftAction';
import { startWritingToMemento } from '@/redux/draft/memento/status';
import { StoreDispatch, StoreState } from '@/redux/types';
import { Button, ButtonLink } from '@/components/common/Button';
import { useFocus } from '@/components/common/focus';
import { Close } from '@/components/common/icons/Close';
import { SHARE_MEMENTO_ROUTE } from '@/components/common/sidebar/sharing/Sharing';
import { SAVE_MEMENTO_ROUTE } from '@/components/common/user-drafts/AsyncUserDraftsDialog';
import { Viewer } from '@/components/draft/Viewer';
import { useMemento } from './useMemento';
import { useSaveMemento } from './useSaveMemento';
import './Memento.scss';

interface MementoPanelProps {
  hideMemento: () => void;
  draft: Draft;
  sharingDraft: SharingDraft;
}

const MementoPanel: FunctionComponent<MementoPanelProps> = ({
  hideMemento,
  draft,
  sharingDraft,
}) => {
  const store = useStore<StoreState>();
  const isLoggedIn = useSelector(getIsLoggedIn);

  const handleDismiss = () => {
    trackFeature('memento-dismiss');
    hideMemento();
    startWritingToMemento();
  };

  const [ref] = useFocus(true, handleDismiss);

  const handleAccept = () => {
    trackFeature('memento-accept');

    (store.dispatch as StoreDispatch)(setDraftAction(draft));

    hideMemento();

    if (sharingDraft.product) {
      addProductToHistory(sharingDraft.product, sharingDraft);
    }

    startWritingToMemento();
  };

  const { productType, appearance, view } = draft;
  const configurationsOnView = getConfigurationsOnView(getConfigurations(draft), view);
  const printArea = getPrintAreaForView(productType, view, configurationsOnView);

  return (
    <CSSTransition
      in
      unmountOnExit
      mountOnEnter
      classNames={{ enterDone: 'memento--visible' }}
      timeout={200}
      appear
    >
      <div className="memento" ref={ref}>
        <div className="memento__header">
          <h2>{t('lastProductDialog.title')}</h2>
          <button className="memento__dismiss" type="button" onClick={handleDismiss}>
            <Close />
          </button>
        </div>
        <p>
          {isLoggedIn
            ? t('lastProductDialog.description.save')
            : t('lastProductDialog.description.share')}
        </p>
        <Viewer
          view={view}
          appearance={appearance}
          printArea={printArea}
          className="memento-viewer"
          configurations={configurationsOnView}
        />
        <Button className="memento__accept" onClick={handleAccept}>
          {t('lastProductDialog.continue')}
        </Button>
        {isCYOMode() && (
          <ButtonLink
            to={isLoggedIn ? SAVE_MEMENTO_ROUTE : SHARE_MEMENTO_ROUTE}
            className="memento__draftButton"
            light
            onClick={() =>
              !isLoggedIn &&
              trackFeature('memento-share', undefined, false, TrackingFeatureTypes.EVENT)
            }
          >
            {t(isLoggedIn ? 'memento.saveDraft' : 'memento.shareDraft')}
          </ButtonLink>
        )}
      </div>
    </CSSTransition>
  );
};

export const Memento: FunctionComponent = () => {
  useSaveMemento();
  const [hideMemento, draft, sharingDraft] = useMemento();

  useEffect(() => {
    trackFeature('memento-shown', undefined, false, TrackingFeatureTypes.EVENT);
  }, []);

  return draft && getProductType(draft) ? (
    <MementoPanel hideMemento={hideMemento!} draft={draft!} sharingDraft={sharingDraft!} />
  ) : null;
};
